import { Column, ContractStatusBadge, Row, Typography } from '@/components'
import { Breakpoint, Categories, ThemeColor } from '@/enums'
import { paths } from '@/routes/paths'
import { theme } from '@/theme'
import { ContractStatus } from '@/types/insurance'
import { formatMoney, useGoToPath, useIsMobile } from '@/utils'
import { ChevronRight, MapPin } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  CardCarrierLogo,
  CardWrapper,
  CategoryLabelRow,
  CoverageBottomSection,
  CoverageCardHeaderRow,
  CoverageTopSection,
  HeaderTextColumn,
  PaymentPeriodText,
  RiskInsuredColumn,
  StatusRow,
} from '../styles'

interface CoverageCardHeaderProps {
  carrierName: string
  categoryLabel: string
  logoUrl: string
  status: ContractStatus | null
}

const CoverageCardHeader = ({
  carrierName,
  categoryLabel,
  logoUrl,
  status,
}: CoverageCardHeaderProps) => {
  return (
    <CoverageTopSection>
      <CoverageCardHeaderRow>
        <HeaderTextColumn>
          <Typography ellipsis color={ThemeColor.b50} variant="captionR">
            {carrierName}
          </Typography>
          <CategoryLabelRow>
            <Typography title={categoryLabel} ellipsis bold variant="p1Body">
              {categoryLabel}
            </Typography>
            <ChevronRight color={theme.color.b50} size={16} />
          </CategoryLabelRow>
        </HeaderTextColumn>
        <CardCarrierLogo name={logoUrl} width="32px" height="32px" />
      </CoverageCardHeaderRow>
      <StatusRow>{status && <ContractStatusBadge status={status} />}</StatusRow>
    </CoverageTopSection>
  )
}

const MobileCoverageCardHeader = ({
  carrierName,
  categoryLabel,
  logoUrl,
  status,
}: CoverageCardHeaderProps) => {
  return (
    <CoverageTopSection>
      <CoverageCardHeaderRow
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        gap="16px"
      >
        <HeaderTextColumn>
          <Row alignItems="center" gap="16px">
            <CardCarrierLogo name={logoUrl} width="32px" height="32px" />
            <Typography ellipsis color={ThemeColor.b50} variant="captionR">
              {carrierName}
            </Typography>
          </Row>
          <CategoryLabelRow>
            <Typography title={categoryLabel} ellipsis bold variant="p1Body">
              {categoryLabel}
            </Typography>
            <ChevronRight color={theme.color.b50} size={16} />
          </CategoryLabelRow>
        </HeaderTextColumn>
      </CoverageCardHeaderRow>
      <StatusRow>{status && <ContractStatusBadge status={status} />}</StatusRow>
    </CoverageTopSection>
  )
}

interface CoverageCardProps {
  carrierName: string
  categoryId: string
  logoUrl: string
  status: ContractStatus | null
  grossPrice: number | null
  risksInsured: string | null
  paymentPeriod: string | null
  insuranceId: string
  locationId: string
  getLocationLabel: (risksInsured: string) => string
}

export const CoverageCard: React.FC<CoverageCardProps> = ({
  carrierName,
  categoryId,
  logoUrl,
  status,
  grossPrice,
  paymentPeriod,
  risksInsured,
  insuranceId,
  locationId,
  getLocationLabel,
}) => {
  const { t } = useTranslation()
  const category = Categories[categoryId as keyof typeof Categories]
  const goToInsurance = useGoToPath(
    paths.viewInsuranceWithKey({
      insuranceId,
      locationId,
      categoryId,
    }),
  )

  const { isMobile } = useIsMobile(Breakpoint.xs)

  const HeaderComponent = isMobile
    ? MobileCoverageCardHeader
    : CoverageCardHeader

  return (
    <CardWrapper onClick={goToInsurance}>
      <HeaderComponent
        carrierName={carrierName}
        categoryLabel={t(`categories.${category.key}.name`)}
        logoUrl={logoUrl}
        status={status}
      />
      <CoverageBottomSection>
        {grossPrice && (
          <Column gap="4px">
            <Typography color={ThemeColor.b50} variant="p2Body">
              {t('common.price')}
            </Typography>
            <Row alignItems="center" gap="4px">
              <Typography bold variant="h5">
                {formatMoney(grossPrice)}
              </Typography>
              {paymentPeriod && (
                <PaymentPeriodText variant="captionR">
                  / {t(`paymentPeriodNouns.${paymentPeriod}`)}
                </PaymentPeriodText>
              )}
            </Row>
          </Column>
        )}
        <RiskInsuredColumn>
          <Typography color={ThemeColor.b50} variant="p2Body">
            {t('locationPage.riskInsured')}
          </Typography>
          <Row alignItems="center" gap="4px">
            <MapPin width={16} height={16} />
            <Typography variant="p1Body" ellipsis>
              {getLocationLabel(risksInsured || '') || risksInsured}
            </Typography>
          </Row>
        </RiskInsuredColumn>
      </CoverageBottomSection>
    </CardWrapper>
  )
}
